import {
  ArrowUpRightIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { svgIcons } from "helpers/images";

export default function EditorParser({ data }) {
  const HtmlRenderer = ({ html, as: Tag = "span", className = "" }) => {
    if (!html) return null;
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const Heading = ({ level, text }) => {
    const Tag = `h${level}`;
    const sizes = {
      1: "text-xl landing-sm:text-2xl font-bold",
      2: "text-xl landing-sm:text-xl font-semibold",
      3: "text-lg font-semibold",
      4: "text-base font-bold",
      5: "text-xs landing-sm:text-sm font-medium",
      6: "text-xs landing-sm:text-xs font-medium",
    };
    return (
      <HtmlRenderer
        as={Tag}
        className={`${sizes[level]} mt-6 px-4 w-full leading-normal`}
        html={text}
      />
    );
  };

  const Paragraph = ({ text, alignment = "left", styleClass = "mt-4" }) => {
    return (
      <HtmlRenderer
        as="p"
        className={`text-base leading-6 px-4 min-w-full text-${alignment} ${styleClass} break-words overrideTailwind`}
        html={text}
      />
    );
  };

  const Table = ({ data }) => {
    if (!data?.content?.length) return null;

    const hasHeader = data.withHeadings === true;

    return (
      <div className={`overflow-auto  mt-8 w-full`}>
        <table className="table-auto w-full border-y border-grey-border">
          <tbody>
            {data.content.map((row, rowIndex) => {
              const isHeaderRow = hasHeader && rowIndex === 0;

              return (
                <tr
                  key={rowIndex}
                  className={`${isHeaderRow ? "bg-[#f9f9fb]" : "border-t border-grey-border"}`}
                >
                  {row.map((cell, cellIndex) => {
                    const CellTag = isHeaderRow ? "th" : "td";
                    return (
                      <HtmlRenderer
                        key={cellIndex}
                        as={CellTag}
                        className={`px-4 py-2 text-left overrideTailwind ${
                          isHeaderRow ? "text-base font-bold" : "text-sm"
                        } ${cellIndex !== row.length - 1 ? "border-r border-grey-border" : ""}`}
                        html={cell}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const List = ({ data }) => {
    const { style = "ordered", meta = {}, items = [] } = data;
    const { counterType = "numeric", start = 1 } = meta;

    return (
      <div className="w-full overrideTailwind">
        {style === "ordered" ? (
          <ol type={"A"} start={start} style={{ listStyleType: counterType }}>
            {items.map((item, index) => {
              const content = typeof item === "string" ? item : item.content;
              const nestedItems = typeof item === "object" ? item.items : null;

              return (
                <li key={index} style={{ margin: "0px" }}>
                  <HtmlRenderer html={content} as="span" />
                  {nestedItems && nestedItems.length > 0 && (
                    <List data={{ ...item, style, meta }} />
                  )}
                </li>
              );
            })}
          </ol>
        ) : (
          <ul>
            {items.map((item, index) => {
              const content = typeof item === "string" ? item : item.content;
              const nestedItems = typeof item === "object" ? item.items : null;

              return (
                <li key={index} style={{ margin: "0px" }}>
                  <HtmlRenderer html={content} as="span" />
                  {nestedItems && nestedItems.length > 0 && <List data={item} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const Checklist = ({ items }) => {
    return (
      <ul className={`space-y-2  mt-8 px-4 w-full`}>
        {items.map((item, i) => {
          const label = item.content || item.text || "";

          return (
            <li key={i}>
              <div className="flex gap-2">
                <span>{"\u2705"}</span>
                <HtmlRenderer html={label} as="p" />
              </div>

              {item.items?.length > 0 && (
                <div className="pl-6 mt-1">
                  <Checklist items={item.items} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const Warning = ({ title, message }) => {
    return (
      <div className={` mt-6 w-full px-4`}>
        <div className="flex gap-2 p-3 !pl-2 bg-yellow-50 rounded-lg">
          <span className="flex items-center justify-center w-6 h-6 min-w-6 min-h-6 bg-yellow-dark rounded-full">
            <ExclamationTriangleIcon className="w-4 stroke-2 text-white" />
          </span>
          <div className="text-base">
            <HtmlRenderer as="p" className="font-bold" html={title} />
            <HtmlRenderer as="p" className="font-medium" html={message} />
          </div>
        </div>
      </div>
    );
  };

  const Callout = ({ data }) => {
    return (
      <div className={` mt-8 w-full px-4`}>
        <div className="flex gap-2 p-3 !pl-2 bg-grey-50 rounded-lg">
          <p className="text-2xl">💡</p>
          <HtmlRenderer as="p" className="font-bold overrideTailwind" html={data?.code} />
        </div>
      </div>
    );
  };

  function Attachment({ data }) {
    if (!data?.file?.url) return null;
    const fileName = data.title || "File";

    return (
      <div className={`w-full  mt-6 px-4`}>
        <a
          href={data.file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex justify-between p-2 rounded-lg border-card-width border-grey-border cursor-pointer"
        >
          <div className="flex gap-2">
            <PaperClipIcon className="w-4 text-grey-600 stroke-2" />

            <HtmlRenderer as="p" className="text-sm font-medium" html={fileName} />
          </div>
          <div className="flex items-center w-5 h-5 p-1 rounded-md bg-grey-25">
            <ArrowUpRightIcon className="w-3" />
          </div>
        </a>
      </div>
    );
  }
  function ImageBlock({ data, link = null }) {
    const {
      file = {},
      caption = "",
      withBorder = false,
      withBackground = false,
      stretched = false,
    } = data;

    let baseClass = `flex justify-center ${!stretched && "max-w-[920px]"} rounded-md`;
    if (withBorder) baseClass += " border border-grey-border";
    if (stretched) baseClass += " w-full";
    if (withBackground) baseClass += " p-4 bg-grey-50 w-full ";

    const imageElement = (
      <img
        src={file.url}
        alt={caption || "Image"}
        style={{ width: withBackground ? "60%" : "100%" }}
      />
    );

    return (
      <div className="flex flex-col items-center justify-center w-full mt-8">
        <div className={`${baseClass} ${link ? "cursor-pointer" : ""}`}>
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {imageElement}
            </a>
          ) : (
            imageElement
          )}
        </div>
      </div>
    );
  }

  const Quote = ({ data }) => {
    const { text = "", caption = "", alignment = "left" } = data;

    return (
      <div className="flex gap-2 w-full mt-8 px-4">
        {<svgIcons.quote className="w-7 rotate-180 mt-1" />}
        <div className={`space-y-2 text-${alignment}`}>
          <HtmlRenderer as="div" className="text-2xl font-bold" html={text} />
          {caption && <HtmlRenderer as="div" className="text-xs" html={caption} />}
        </div>
      </div>
    );
  };

  const Toggle = ({ data, contents }) => {
    const isOpen = data.status === "open";

    return (
      <details className={`text-sm  w-full mt-6 px-4`} open={isOpen}>
        <summary className="primary cursor-pointer">{data.text}</summary>
        {contents.map((content) => (
          <Paragraph
            key={content?.id}
            text={content?.data?.text}
            alignment={content?.data?.alignment}
            styleClass="mt-0"
          />
        ))}
      </details>
    );
  };

  const alertTypeClasses = {
    primary: "bg-[#ebf8ff] text-[#2b6cb0] border border-[#4299e1]",
    secondary: "bg-[#f7fafc] text-black border border-[#cbd5e0]",
    info: "bg-[#e6fdff] text-[#00727c] border border-[#4cd4ce]",
    success: "bg-[#f0fff4] text-[#2f855a] border border-[#68d391]",
    warning: "bg-[#fffaf0] text-[#c05621] border border-[#ed8936]",
    danger: "bg-[#fff5f5] text-[#c53030] border border-[#fc8181]",
    light: "bg-white text-black border border-[#edf2f7]",
    dark: "bg-[#2d3748] text-[#d3d3d3] border border-black",
  };

  const Alert = ({ type = "info", align = "left", message = "" }) => {
    const typeClass = alertTypeClasses[type] || alertTypeClasses.info;

    return (
      <div className={`w-full  mt-6 px-4`}>
        <HtmlRenderer
          as="div"
          className={`min-w-full p-3 rounded-md ${typeClass} text-${align} overrideTailwind`}
          html={message}
        />
      </div>
    );
  };

  const Delimiter = ({ data }) => {
    const { style, lineWidth = 100, lineThickness = 1 } = data;

    if (style === "star") {
      return (
        <div className="text-center mt-4">
          <span className="text-3xl tracking-widest font-black">***</span>
        </div>
      );
    }

    if (style === "dash") {
      return (
        <div className="text-center mt-4">
          <span className="text-3xl font-black tracking-widest">---</span>
        </div>
      );
    }

    if (style === "line") {
      return (
        <div className="flex justify-center mt-4">
          <div
            className="bg-black rounded-sm"
            style={{
              width: `${lineWidth}%`,
              height: `${lineThickness}px`,
            }}
          />
        </div>
      );
    }
    return null;
  };

  const renderBlocks = (blocks) => {
    const elements = [];

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const { type, data, id } = block;

      switch (type) {
        case "header":
          elements.push(<Heading key={id} level={data.level} index={i} text={data.text} />);
          break;

        case "paragraph":
          elements.push(
            <Paragraph key={id} text={data.text} alignment={data.alignment} index={i} />
          );
          break;

        case "table":
          elements.push(<Table key={id} data={data} index={i} />);
          break;

        case "list":
          if (data.style === "checklist") {
            elements.push(<Checklist key={id} items={data.items} index={i} />);
          } else {
            elements.push(
              <div className="mt-4 px-4 w-full">
                <List key={id} data={data} />
              </div>
            );
          }
          break;

        case "checklist":
          elements.push(<Checklist key={id} items={data.items} index={i} />);
          break;

        case "warning":
          elements.push(<Warning key={id} title={data.title} message={data.message} index={i} />);
          break;

        case "attaches":
          elements.push(<Attachment key={id} data={data} index={i} />);
          break;

        case "image": {
          const nextBlock = blocks[i + 1];
          let link = null;
          if (nextBlock?.type === "paragraph" && nextBlock?.data?.text) {
            const anchorTagMatch = nextBlock.data.text.match(
              /<a[^>]+href="([^"]+)"[^>]*>(.*?)<\/a>/i
            );
            if (anchorTagMatch) {
              const [, href, linkText] = anchorTagMatch;
              if (linkText?.trim().toLowerCase() === "'''banner-image-cta'''") {
                link = href;
                i++;
              }
            }
          }

          elements.push(<ImageBlock key={id} data={data} link={link} index={i} />);
          break;
        }

        case "quote":
          elements.push(<Quote key={id} data={data} index={i} />);
          break;

        case "code":
          elements.push(<Callout key={id} data={data} index={i} />);
          break;

        case "toggle": {
          const count = data.items || 0;
          if (blocks.length > i + count) {
            const contentBlocks = blocks.slice(i + 1, i + count + 1);

            elements.push(<Toggle key={id} data={data} contents={contentBlocks} index={i} />);

            i += count;
          }
          break;
        }
        case "alert":
          elements.push(
            <Alert key={id} type={data.type} align={data.align} message={data.message} index={i} />
          );
          break;

        case "delimiter":
          elements.push(<Delimiter key={i} data={data} />);
          break;

        default:
          break;
      }
    }

    return elements;
  };

  return <div className="flex flex-col w-full text-base">{renderBlocks(data.blocks)}</div>;
}
